import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, FileText, Award, Users, Calendar, AlertTriangle, ArrowRight, CheckCircle2, MapPin, Clock, Plus, Building2, UserPlus, CheckSquare, Target, Activity, ChevronRight } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatDate } from "@/lib/format";
import { STAGE_STYLES, CHART_COLORS } from "@/lib/theme";
import { StatusBadge } from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { CompanyFormDialog } from "@/components/CompanyFormDialog";
import { ContactFormDialog } from "@/components/ContactFormDialog";
import { TaskFormDialog } from "@/components/TaskFormDialog";

const ICONS = { TrendingUp, FileText, Award, Users, Calendar, AlertTriangle };
const FUNNEL_STAGES = ["New Lead", "Contacted", "Qualified", "Site Visit", "Quotation", "Negotiation", "Won"];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState(null);
  const [period, setPeriod] = useState("month");
  const [dialog, setDialog] = useState(null);

  const loadStats = useCallback(() => { api.get("/dashboard/stats").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { api.get(`/dashboard/activity?period=${period}`).then((r) => setActivity(r.data)).catch(() => {}); }, [period]);

  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const firstName = (user?.name || "").split(" ")[0];
  const goStage = (stage) => navigate("/leads", { state: { stage, view: "table", ts: Date.now() } });
  const goTasks = (tab) => navigate("/tasks", { state: { tab, ts: Date.now() } });

  if (!data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3"><Skeleton className="h-80 rounded-xl lg:col-span-2" /><Skeleton className="h-80 rounded-xl" /></div>
      </div>
    );
  }

  const ta = data.today_actions || {};
  const funnel = FUNNEL_STAGES.map((s) => data.pipeline.find((p) => p.stage === s) || { stage: s, count: 0, value: 0 });
  const maxFunnel = Math.max(...funnel.map((f) => f.count), 1);
  const pipelineEmpty = data.pipeline.every((p) => p.count === 0);

  return (
    <div className="space-y-6">
      {/* Header + Quick Actions */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">{greeting()}, {firstName} 👋</h1>
          <p className="mt-1 text-sm text-slate-500">Here's your industrial flooring pipeline overview · {today}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="quick-actions">
          {user?.role !== "ACCOUNTS" && <>
            <QuickAction icon={Plus} label="New Lead" primary onClick={() => setDialog("lead")} testid="quick-new-lead" />
            <QuickAction icon={Building2} label="Company" onClick={() => setDialog("company")} testid="quick-new-company" />
            <QuickAction icon={UserPlus} label="Contact" onClick={() => setDialog("contact")} testid="quick-new-contact" />
            <QuickAction icon={CheckSquare} label="Task" onClick={() => setDialog("task")} testid="quick-new-task" />
          </>}
        </div>
      </div>

      {/* Today's Actions */}
      <div>
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500"><Clock className="h-4 w-4" /> Today's Actions</h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="todays-actions">
          <ActionCard testid="action-overdue" icon={AlertTriangle} label="Overdue Follow-ups" value={ta.overdue || 0} tone={ta.overdue > 0 ? "red" : "neutral"} onClick={() => goTasks("overdue")} empty="All caught up" />
          <ActionCard testid="action-today" icon={Calendar} label="Follow-ups Today" value={ta.follow_today || 0} tone="blue" onClick={() => goTasks("today")} empty="Nothing due today" />
          <ActionCard testid="action-quotations" icon={FileText} label="Quotations Awaiting" value={ta.quotations_awaiting?.count || 0} sub={ta.quotations_awaiting?.value ? formatINR(ta.quotations_awaiting.value) : null} tone="blue" onClick={() => goStage("Quotation")} empty="No open quotes" />
          <ActionCard testid="action-sitevisits" icon={MapPin} label="Site Visit Stage" value={ta.site_visits?.count || 0} sub={ta.site_visits?.value ? formatINR(ta.site_visits.value) : null} tone="blue" onClick={() => goStage("Site Visit")} empty="None scheduled" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {data.kpis.map((k) => <KpiCard key={k.key} kpi={k} onClick={() => k.key === "overdue" ? goTasks("overdue") : k.key === "follow_today" ? goTasks("today") : k.key === "won_value" ? goStage("Won") : k.key === "quoted_value" ? goStage("Quotation") : navigate("/leads")} />)}
      </div>

      {/* Funnel + Sources */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="sales-funnel">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-800">Sales Funnel</h3>
            <span className="text-xs text-slate-400">Click a stage to filter leads</span>
          </div>
          {pipelineEmpty ? (
            <EmptyBlock icon={Target} title="Your pipeline is currently empty" desc="Create your first lead to start tracking opportunities through the funnel." action={() => setDialog("lead")} actionLabel="New Lead" />
          ) : (
            <div className="space-y-2">
              {funnel.map((f) => {
                const style = STAGE_STYLES[f.stage] || {};
                const width = 30 + (f.count / maxFunnel) * 70;
                return (
                  <button key={f.stage} data-testid={`funnel-${f.stage.toLowerCase().replace(/ /g, "-")}`} onClick={() => goStage(f.stage)}
                    className="group flex w-full items-center gap-3 text-left">
                    <div className="w-28 shrink-0 text-right text-xs font-semibold text-slate-600">{f.stage}</div>
                    <div className="relative flex-1">
                      <div className="flex h-10 items-center justify-between rounded-lg px-3 text-white transition-all duration-300 group-hover:brightness-110"
                        style={{ width: `${width}%`, backgroundColor: style.dot || "#0057A8", minWidth: "120px" }}>
                        <span className="text-sm font-bold">{f.count}</span>
                        <span className="font-mono text-xs font-semibold opacity-90">{formatINR(f.value)}</span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 opacity-0 transition group-hover:opacity-100" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="mb-4 text-base font-semibold text-slate-800">Lead Sources</h3>
          {data.sources.length === 0 ? (
            <EmptyBlock icon={Users} title="No source data yet" desc="Lead sources appear once you add leads." small />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={data.sources} dataKey="count" nameKey="source" cx="50%" cy="50%" innerRadius={42} outerRadius={72} paddingAngle={2}>
                    {data.sources.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1.5">
                {data.sources.slice(0, 5).map((s, i) => (
                  <div key={s.source} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-slate-600"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />{s.source}</span>
                    <span className="font-semibold text-slate-700">{s.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sales Activity */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="sales-activity">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800"><Activity className="h-4 w-4 text-[#0057A8]" /> Sales Activity</h3>
          <div className="flex rounded-lg border border-slate-200 p-0.5">
            {[["week", "This Week"], ["month", "This Month"], ["quarter", "This Quarter"]].map(([k, label]) => (
              <button key={k} data-testid={`activity-period-${k}`} onClick={() => setPeriod(k)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${period === k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700"}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <ActivityStat label="Leads Created" value={activity?.leads_created} icon={Target} color="#0057A8" />
          <ActivityStat label="Follow-ups Completed" value={activity?.follow_ups_completed} icon={CheckCircle2} color="#059669" />
          <ActivityStat label="Quotations Created" value={activity?.quotations_created} icon={FileText} color="#0284C7" />
          <ActivityStat label="Deals Won" value={activity?.deals_won} icon={Award} color="#059669" />
        </div>
      </div>

      {/* Recent Leads */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="recent-leads">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Recent Leads</h3>
          <button onClick={() => navigate("/leads")} className="flex items-center gap-1 text-xs font-medium text-[#0057A8]">View all <ArrowRight className="h-3 w-3" /></button>
        </div>
        {data.recent_leads.length === 0 ? (
          <EmptyBlock icon={Target} title="No active opportunities" desc="New leads you create will show up here." action={() => setDialog("lead")} actionLabel="New Lead" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <th className="py-2 pr-3">Lead / Company</th>
                  <th className="px-3 py-2 text-right">Est. Value</th>
                  <th className="px-3 py-2">Stage</th>
                  <th className="hidden px-3 py-2 md:table-cell">Assigned</th>
                  <th className="hidden px-3 py-2 lg:table-cell">Next Follow-up</th>
                  <th className="hidden px-3 py-2 lg:table-cell">Next Action</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_leads.map((l) => {
                  const overdueFollow = l.next_follow_up && new Date(l.next_follow_up) < new Date(new Date().toDateString());
                  return (
                    <tr key={l.id} data-testid={`recent-lead-${l.id}`} onClick={() => navigate(`/leads/${l.id}`)} className="cursor-pointer border-b border-slate-50 transition hover:bg-slate-50/70">
                      <td className="py-3 pr-3"><div className="font-semibold text-slate-800">{l.company_name}</div><div className="text-xs text-slate-400">{l.name}</div></td>
                      <td className="px-3 py-3 text-right font-mono font-semibold text-slate-800">{formatINR(l.estimated_value)}</td>
                      <td className="px-3 py-3"><StatusBadge value={l.stage} dot /></td>
                      <td className="hidden px-3 py-3 text-slate-600 md:table-cell">{(l.assigned_to_name || "—").split(" ")[0]}</td>
                      <td className="hidden px-3 py-3 lg:table-cell"><span className={`text-xs ${overdueFollow ? "font-semibold text-red-600" : "text-slate-500"}`}>{formatDate(l.next_follow_up)}</span></td>
                      <td className="hidden px-3 py-3 lg:table-cell"><span className="text-xs text-slate-500">{l.next_action || "—"}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <LeadFormDialog open={dialog === "lead"} onOpenChange={(o) => !o && setDialog(null)} onSaved={loadStats} />
      <CompanyFormDialog open={dialog === "company"} onOpenChange={(o) => !o && setDialog(null)} onSaved={loadStats} />
      <ContactFormDialog open={dialog === "contact"} onOpenChange={(o) => !o && setDialog(null)} onSaved={loadStats} />
      <TaskFormDialog open={dialog === "task"} onOpenChange={(o) => !o && setDialog(null)} onSaved={loadStats} />
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, primary, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${primary ? "text-white shadow-sm hover:brightness-110" : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"}`}
      style={primary ? { backgroundColor: "#0057A8" } : {}}>
      <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{label}</span><span className="sm:hidden">{label.replace("New ", "")}</span>
    </button>
  );
}

function ActionCard({ icon: Icon, label, value, sub, tone, onClick, empty, testid }) {
  const colors = { red: { bg: "#FEF2F2", fg: "#E31E24" }, blue: { bg: "#EFF6FF", fg: "#0057A8" }, neutral: { bg: "#F1F5F9", fg: "#64748B" } };
  const c = colors[tone] || colors.neutral;
  const isZero = !value;
  return (
    <button data-testid={testid} onClick={onClick} className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-all duration-200 hover:border-slate-300 hover:shadow-md">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: c.bg, color: c.fg }}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <div className="text-2xl font-extrabold leading-none" style={{ color: isZero ? "#94A3B8" : c.fg }}>{value}</div>
        <div className="mt-1 truncate text-xs font-medium text-slate-500">{isZero ? empty : label}</div>
        {sub && !isZero && <div className="font-mono text-[11px] text-slate-400">{sub}</div>}
      </div>
      <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-slate-300 opacity-0 transition group-hover:opacity-100" />
    </button>
  );
}

function KpiCard({ kpi, onClick }) {
  const Icon = ICONS[kpi.icon] || TrendingUp;
  const isCurrency = kpi.type === "currency";
  const danger = kpi.trend === "danger" && kpi.value > 0;
  const alert = kpi.trend === "alert" && kpi.value > 0;
  const accent = danger ? "#E31E24" : alert ? "#EA580C" : "#0057A8";
  return (
    <button data-testid={`kpi-${kpi.key}`} onClick={onClick} className="group rounded-xl border border-slate-200 bg-white p-5 text-left transition-all duration-200 hover:border-slate-300 hover:shadow-md">
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{kpi.title}</span>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: `${accent}14`, color: accent }}><Icon className="h-[18px] w-[18px]" /></div>
      </div>
      <div className="mt-3 font-mono text-2xl font-extrabold tracking-tight text-slate-900 md:text-[28px]">{isCurrency ? formatINR(kpi.value) : kpi.value.toLocaleString("en-IN")}</div>
      <div className="mt-1.5 flex items-center gap-2">
        {kpi.change != null && (
          <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-bold ${kpi.change >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
            {kpi.change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(kpi.change)}%
          </span>
        )}
        <span className="text-xs text-slate-400">{kpi.subtext}</span>
      </div>
    </button>
  );
}

function ActivityStat({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: `${color}14`, color }}><Icon className="h-4 w-4" /></div>
        <span className="font-mono text-2xl font-extrabold text-slate-900">{value ?? "—"}</span>
      </div>
      <div className="mt-2 text-xs font-medium text-slate-500">{label}</div>
    </div>
  );
}

function EmptyBlock({ icon: Icon, title, desc, action, actionLabel, small }) {
  return (
    <div className={`flex flex-col items-center justify-center text-center ${small ? "py-8" : "py-12"}`}>
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-300"><Icon className="h-6 w-6" /></div>
      <p className="text-sm font-semibold text-slate-700">{title}</p>
      <p className="mt-1 max-w-xs text-xs text-slate-400">{desc}</p>
      {action && <button onClick={action} className="mt-4 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-white" style={{ backgroundColor: "#0057A8" }}><Plus className="h-3.5 w-3.5" /> {actionLabel}</button>}
    </div>
  );
}
